import { useEffect, useState } from "react";
import styles from "../../../styles/components/BillingSection.module.css";
import StripeCheckoutModal from "../billing/StripeCheckoutModal";
import PaymentMethodsSection from "../billing/PaymentMethodsSection";
import {
  getAccountInvoices,
  retryPayment,
} from "../../../services/billing.service";

const ENTITLEMENT_STATUS_LABELS = {
  active: "Activo",
  past_due: "Pago pendiente",
  canceled: "Cancelado",
  inactive: "Inactivo",
};

const INVOICE_STATUS_LABELS = {
  paid: "Pagada",
  open: "Pendiente",
  draft: "Borrador",
  void: "Anulada",
};

function formatDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatAmount(amount, currency) {
  if (amount == null) return "-";
  return `$${Number(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${(currency || "USD").toUpperCase()}`;
}

function InvoiceStatusBadge({ status }) {
  return (
    <span className={`${styles.invoiceStatusBadge} ${styles[`invoiceStatus_${status}`]}`}>
      {INVOICE_STATUS_LABELS[status] || status}
    </span>
  );
}

function InvoiceLinks({ hostedUrl, pdfUrl }) {
  if (!hostedUrl && !pdfUrl) return null;
  return (
    <div className={styles.invoiceLinks}>
      {hostedUrl && (
        <a href={hostedUrl} target="_blank" rel="noreferrer" className={styles.invoiceLink}>
          Ver
        </a>
      )}
      {pdfUrl && (
        <a href={pdfUrl} target="_blank" rel="noreferrer" className={styles.invoiceLink}>
          PDF
        </a>
      )}
    </div>
  );
}

const BillingSection = ({
  accounts = [],
  selectedAccount,
  onSelectedAccountChange,
  summary,
  summaryLoading,
  refreshSummary,
}) => {
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [error, setError] = useState("");
  const [retryLoading, setRetryLoading] = useState(false);
  const [retryError, setRetryError] = useState("");
  const [checkoutClientSecret, setCheckoutClientSecret] = useState("");
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);

  useEffect(() => {
    if (!selectedAccount?.id) {
      setInvoices([]);
      setError("");
      return;
    }

    const fetchInvoices = async () => {
      try {
        setInvoicesLoading(true);
        setError("");
        const invoicesData = await getAccountInvoices(selectedAccount.id);
        setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
      } catch (err) {
        console.error("Error cargando facturas:", err);
        setError("No se pudo cargar el historial de facturas.");
      } finally {
        setInvoicesLoading(false);
      }
    };

    fetchInvoices();
  }, [selectedAccount]);

  const refreshBillingData = async () => {
    try {
      setInvoicesLoading(true);
      const [invoicesData] = await Promise.all([
        getAccountInvoices(selectedAccount.id),
        refreshSummary?.(),
      ]);
      setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleRetryPayment = async () => {
    try {
      setRetryLoading(true);
      setRetryError("");
      const intent = await retryPayment(selectedAccount.id);
      const clientSecret = intent?.client_secret;
      if (!clientSecret) throw new Error("No se recibió client_secret.");
      setCheckoutClientSecret(clientSecret);
      setCheckoutModalOpen(true);
    } catch (err) {
      console.error("Error reintentando pago:", err);
      setRetryError(err.message || "No se pudo reintentar el pago.");
    } finally {
      setRetryLoading(false);
    }
  };

  if (!selectedAccount) {
    return (
      <div className={styles.sectionContent}>
        <div className={styles.accountsList}>
          {accounts.length === 0 && (
            <div className={styles.emptyState}>No hay cuentas disponibles.</div>
          )}
          {accounts.map((account) => (
            <div key={account.id} className={styles.accountItem}>
              <button
                type="button"
                className={styles.accountCardButton}
                onClick={() => onSelectedAccountChange?.(account)}
              >
                <div className={styles.accountCard}>
                  <div className={styles.accountLeft}>
                    <div className={styles.accountLogo}>
                      {(account.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className={styles.accountInfo}>
                      <div className={styles.accountName}>{account.name || "Sin nombre"}</div>
                    </div>
                  </div>
                  <span className={styles.linkButton}>Ver facturas</span>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const isLoading = summaryLoading || invoicesLoading;

  return (
    <div className={styles.billingScreen}>
      {isLoading ? (
        <div className={styles.stateBox}>Cargando...</div>
      ) : error ? (
        <div className={styles.stateBoxError}>{error}</div>
      ) : (
        <>
          {summary && (
            <div className={styles.summaryCard}>
              <div className={styles.summaryRow}>
                <div className={styles.summaryField}>
                  <span className={styles.fieldLabel}>Plan</span>
                  <span className={styles.fieldValue}>
                    {summary.plan_name || summary.plan || summary.last_plan_name || summary.last_plan || "-"}
                  </span>
                </div>
                <div className={styles.summaryField}>
                  <span className={styles.fieldLabel}>Estado</span>
                  <span
                    className={`${styles.statusBadge} ${styles[`entitlementStatus_${summary.entitlement_status}`]}`}
                  >
                    {ENTITLEMENT_STATUS_LABELS[summary.entitlement_status] ||
                      summary.entitlement_status ||
                      "-"}
                  </span>
                </div>
                <div className={styles.summaryField}>
                  <span className={styles.fieldLabel}>Válido hasta</span>
                  <span className={styles.fieldValue}>
                    {formatDate(summary.valid_until)}
                  </span>
                </div>
              </div>

              {summary.has_scheduled_change && (
                <div className={styles.scheduledChangeNotice}>
                  Cambio programado a{" "}
                  <strong>{summary.scheduled_plan_name || summary.scheduled_plan}</strong>{" "}
                  el {formatDate(summary.scheduled_effective_at)}
                </div>
              )}

              {(summary.entitlement_status === "past_due" ||
                summary.entitlement_status === "inactive") && (
                <div className={styles.retryRow}>
                  <button
                    type="button"
                    className={styles.retryButton}
                    onClick={handleRetryPayment}
                    disabled={retryLoading}
                  >
                    {retryLoading ? "Procesando..." : "Reintentar pago"}
                  </button>
                  {retryError && (
                    <span className={styles.retryError}>{retryError}</span>
                  )}
                </div>
              )}

              {summary.last_invoice && (
                <div className={styles.lastInvoiceRow}>
                  <span className={styles.lastInvoiceLabel}>Última factura</span>
                  <span className={styles.lastInvoiceAmount}>
                    {formatAmount(
                      summary.last_invoice.amount_due,
                      summary.last_invoice.currency,
                    )}
                  </span>
                  <InvoiceStatusBadge status={summary.last_invoice.status} />
                  <InvoiceLinks
                    hostedUrl={summary.last_invoice.hosted_invoice_url}
                    pdfUrl={summary.last_invoice.invoice_pdf_url}
                  />
                </div>
              )}
            </div>
          )}

          <PaymentMethodsSection accountId={selectedAccount.id} />

          <div className={styles.invoicesBlock}>
            <div className={styles.invoicesBlockTitle}>Historial de facturas</div>

            {invoices.length === 0 ? (
              <div className={styles.emptyState}>Sin facturas registradas.</div>
            ) : (
              <div className={styles.invoicesTable}>
                <div className={styles.invoicesTableHeader}>
                  <span>Número</span>
                  <span>Fecha</span>
                  <span>Monto</span>
                  <span>Estado</span>
                  <span />
                </div>
                {invoices.map((invoice) => (
                  <div key={invoice.invoice_id} className={styles.invoiceRow}>
                    <span className={styles.invoiceNumber}>
                      {invoice.number || invoice.invoice_id}
                    </span>
                    <span className={styles.invoiceDate}>
                      {formatDate(invoice.created_at)}
                    </span>
                    <span className={styles.invoiceAmount}>
                      {formatAmount(invoice.amount, invoice.currency)}
                    </span>
                    <InvoiceStatusBadge status={invoice.status} />
                    <InvoiceLinks
                      hostedUrl={invoice.hosted_invoice_url}
                      pdfUrl={invoice.invoice_pdf_url}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      <StripeCheckoutModal
        open={checkoutModalOpen}
        title="Reintentar pago"
        clientSecret={checkoutClientSecret}
        onClose={() => setCheckoutModalOpen(false)}
        onDone={async () => {
          setCheckoutModalOpen(false);
          setCheckoutClientSecret("");
          await refreshBillingData();
        }}
      />
    </div>
  );
};

export default BillingSection;
